import { useState, type FormEvent } from 'react';
import { Button, FormField, Input } from '@/shared/components';
import { DisableTwoFactorDialog } from './DisableTwoFactorDialog';

interface Props {
  recoveryCodesRemaining: number;
  /** Fehler der jeweiligen Aktion als deutscher Satz; null ohne Fehler. */
  regenerateFailure: string | null;
  disableFailure: string | null;
  onRegenerate: (password: string, code: string) => Promise<void>;
  onDisable: (password: string, code: string) => Promise<void>;
  disableOpen: boolean;
  onDisableOpen: (open: boolean) => void;
}

const CARD = 'border-hairline rounded-tile flex flex-col gap-3 border bg-white p-5';

/** Aktive Zwei-Faktor-Anmeldung (Board „Kontosicherheit“): Stand, neue Codes, Ausschalten im Dialog. */
export function TwoFactorManagePanel({
  recoveryCodesRemaining,
  regenerateFailure,
  disableFailure,
  onRegenerate,
  onDisable,
  disableOpen,
  onDisableOpen,
}: Props) {
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const regenerate = async (event: FormEvent) => {
    event.preventDefault();
    if (!password || !code) return;
    setBusy(true);
    try {
      await onRegenerate(password, code);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-col gap-3">
      <section className={CARD}>
        <div className="flex items-center gap-3">
          <b className="flex-1 font-serif text-xl">Zwei-Faktor-Anmeldung</b>
          <span className="rounded-pill bg-forest-soft text-forest-deep inline-flex h-6 items-center px-2.5 text-[11px] font-bold">
            Aktiv
          </span>
        </div>
        <p className="text-sm">
          Verbleibende Wiederherstellungscodes: <b>{recoveryCodesRemaining}</b>
        </p>
        <p className="text-ink-soft text-xs">
          Änderungen brauchen Passwort und aktuellen Code aus der App.
        </p>
      </section>
      <form className={CARD} onSubmit={(event) => void regenerate(event)}>
        <b className="text-[15px]">Neue Wiederherstellungscodes</b>
        <p className="text-ink-soft text-xs">
          Neue Codes ersetzen alle alten und melden andere Geräte ab. Speichere sie, bevor du die
          Seite verlässt.
        </p>
        {regenerateFailure && (
          <p
            role="alert"
            className="bg-sealwax-tint text-sealwax rounded-control p-3 text-sm font-semibold"
          >
            {regenerateFailure}
          </p>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          <FormField label="Aktuelles Passwort" required>
            <Input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </FormField>
          <FormField label="Code aus der App" required>
            <Input
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          </FormField>
        </div>
        <Button
          type="submit"
          variant="brand"
          size="lg"
          className="self-start"
          loading={busy}
          disabled={!password || !code}
        >
          Neue Wiederherstellungscodes
        </Button>
      </form>
      <section className={CARD}>
        <b className="text-[15px]">Zwei-Faktor ausschalten</b>
        <p className="text-ink-soft text-xs">
          Danach reicht dein Passwort zum Anmelden. Angemeldete Geräte werden dabei abgemeldet.
        </p>
        <Button
          variant="secondary"
          size="lg"
          className="text-sealwax self-start"
          onClick={() => onDisableOpen(true)}
        >
          Zwei-Faktor ausschalten …
        </Button>
      </section>
      <DisableTwoFactorDialog
        open={disableOpen}
        onOpenChange={onDisableOpen}
        onDisable={onDisable}
        failure={disableFailure}
      />
    </div>
  );
}
